import React from 'react';
import * as JsxRuntime from 'react/jsx-runtime';
import axios from 'axios';
import reactPkg from 'react/package.json';
import axiosPkg from 'axios/package.json';

import {
  init,
  loadRemote,
  registerRemotes,
} from '@module-federation/enhanced/runtime';

import { TimeoutError, NotFoundError, NetworkError } from '@/lib/errors';
import { startMfeSpan, endMfeSpan } from '@/lib/perf/mfe-timing';

init({
  name: 'host',
  remotes: [],
  shared: {
    react: {
      version: reactPkg.version,
      scope: 'default',
      lib: () => React,
      shareConfig: {
        singleton: true,
        requiredVersion: '^19.0.0',
      },
    },
    'react/jsx-runtime': {
      version: reactPkg.version,
      scope: 'default',
      lib: () => JsxRuntime,
      shareConfig: {
        singleton: true,
        requiredVersion: '^19.0.0',
      },
    },
    axios: {
      version: axiosPkg.version,
      scope: 'default',
      lib: () => axios,
      shareConfig: {
        singleton: true,
        requiredVersion: '^1.18.0',
      },
    },
  },
});

interface RemoteAppModule {
  default: React.ComponentType<{ basename?: string }>;
}

const promiseCache = new Map<string, Promise<React.ComponentType>>();
const moduleCache = new Map<string, React.ComponentType>();
const registeredRemotes = new Set<string>();
const preloadedRemotes = new Set<string>();

setInterval(
  () => {
    const cacheSize = promiseCache.size;
    const remotesSize = registeredRemotes.size;
    const modulesSize = moduleCache.size;

    if (cacheSize > 10 || remotesSize > 10 || modulesSize > 10) {
      promiseCache.clear();
      registeredRemotes.clear();
      moduleCache.clear();
    }
  },
  5 * 60 * 1000
);

async function fetchManifest(
  url: string,
  signal: AbortSignal
): Promise<Response> {
  return await fetch(`${url}/mf-manifest.json`, {
    signal,
    method: 'HEAD',
    cache: 'no-cache',
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
      Expires: '0',
    },
  });
}

function handleFetchError(
  error: unknown,
  url: string,
  attempt: number,
  maxRetries: number
): void {
  if (error instanceof Error && error.name === 'AbortError') {
    if (attempt === maxRetries) {
      throw new TimeoutError(
        'O aplicativo não respondeu em tempo hábil',
        url,
        maxRetries
      );
    }
    return;
  }

  if (
    error instanceof NotFoundError ||
    error instanceof NetworkError ||
    error instanceof TimeoutError
  ) {
    throw error;
  }

  if (attempt === maxRetries) {
    throw new NetworkError(
      error instanceof Error ? error.message : 'Erro desconhecido ao conectar',
      url
    );
  }
}

function validateResponse(response: Response, url: string): void {
  if (response.ok) {
    return;
  }

  if ([400, 404].includes(response.status)) {
    throw new NotFoundError('Aplicativo não encontrado', url, response.status);
  }

  throw new NetworkError('Erro ao acessar o aplicativo', url, response.status);
}

async function checkRemoteAvailability(
  url: string,
  maxRetries = 2,
  timeout = 5000
): Promise<void> {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetchManifest(url, controller.signal);
      clearTimeout(timeoutId);

      validateResponse(response, url);
      return;
    } catch (error) {
      clearTimeout(timeoutId);
      handleFetchError(error, url, attempt, maxRetries);

      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
  }

  throw new NetworkError(
    `Aplicativo não disponível após ${maxRetries} tentativas`,
    url
  );
}

function isModuleNotFoundError(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.message.includes('remoteEntryExports is undefined') ||
      error.message.includes('Module not found'))
  );
}

function shouldRetryError(error: unknown): boolean {
  return !(error instanceof NotFoundError || error instanceof TimeoutError);
}

function cleanupCaches(route: string, cacheKey: string): void {
  promiseCache.delete(cacheKey);
  registeredRemotes.delete(route);
}

async function registerRemoteApp(route: string, url: string): Promise<void> {
  if (registeredRemotes.has(route)) {
    return;
  }

  startMfeSpan(route, 'availability');
  try {
    await checkRemoteAvailability(url);
  } finally {
    endMfeSpan(route, 'availability');
  }

  const manifestUrl = `${url}/mf-manifest.json?_t=${Date.now()}`;

  registerRemotes([
    {
      name: route,
      entry: manifestUrl,
      shareScope: 'default',
    },
  ]);

  registeredRemotes.add(route);
}

async function loadRemoteModule(
  route: string
): Promise<{ component: React.ComponentType; cacheHit: boolean }> {
  const moduleKey = `${route}/miniapp`;

  if (moduleCache.has(moduleKey)) {
    return { component: moduleCache.get(moduleKey)!, cacheHit: true };
  }

  startMfeSpan(route, 'remoteEntry');
  let RemoteApp: RemoteAppModule | null | undefined;
  try {
    RemoteApp = await loadRemote<RemoteAppModule>(moduleKey);
  } finally {
    endMfeSpan(route, 'remoteEntry');
  }

  if (!RemoteApp?.default) {
    throw new NotFoundError(`Módulo não encontrado: ${route}/miniapp`, '');
  }

  moduleCache.set(moduleKey, RemoteApp.default);
  return { component: RemoteApp.default, cacheHit: false };
}

async function attemptLoad(
  route: string,
  url: string
): Promise<React.ComponentType> {
  startMfeSpan(route, 'load');

  try {
    await registerRemoteApp(route, url);
    const { component, cacheHit } = await loadRemoteModule(route);
    endMfeSpan(route, 'load', { cache_hit: cacheHit });
    recordMicroFrontendUsage(route);
    return component;
  } catch (error) {
    endMfeSpan(route, 'load', {
      error: true,
      error_message: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
}

export async function loadMicroFrontend(
  route: string,
  url: string,
  maxRetries = 1
): Promise<React.ComponentType> {
  const cacheKey = `${route}-${url}`;

  if (promiseCache.has(cacheKey)) {
    return await promiseCache.get(cacheKey)!;
  }

  const promise = (async () => {
    let lastError: Error = new Error('Unknown error');

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await attemptLoad(route, url);
      } catch (error) {
        lastError = error as Error;

        if (!shouldRetryError(error)) {
          cleanupCaches(route, cacheKey);
          throw error;
        }

        if (isModuleNotFoundError(error)) {
          cleanupCaches(route, cacheKey);
          throw new NotFoundError(
            `Módulo ${route}/miniapp não encontrado`,
            url
          );
        }

        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 200));
          registeredRemotes.delete(route);
        }
      }
    }

    cleanupCaches(route, cacheKey);

    if (lastError instanceof NetworkError) {
      throw lastError;
    }

    throw new NetworkError(
      `Falha ao carregar micro frontend após ${maxRetries} tentativas: ${lastError?.message || 'Erro desconhecido'}`,
      url
    );
  })();

  promiseCache.set(cacheKey, promise);
  return promise;
}

export function recordMicroFrontendUsage(route: string): void {
  if (!preloadedRemotes.has(route)) {
    preloadMicroFrontend(route);
  }
}

export async function preloadMicroFrontend(route: string): Promise<void> {
  if (preloadedRemotes.has(route)) {
    return;
  }

  try {
    const { getMicroFrontendUrl, waitForMenu } =
      await import('@/lib/menu-utils');
    await waitForMenu();

    const url = getMicroFrontendUrl(route);
    if (!url) {
      return;
    }

    loadMicroFrontend(route, url, 1)
      .then(() => {
        preloadedRemotes.add(route);
      })
      .catch(error => {
        console.error(`Error preloading micro frontend: ${route}`, error);
        const cacheKey = `${route}-${url}`;
        promiseCache.delete(cacheKey);
      });
  } catch (error) {
    console.error(`Error preloading micro frontend: ${route}`, error);
  }
}

export async function loadAppByRoute(
  route: string
): Promise<React.ComponentType> {
  const { getMicroFrontendUrl, waitForMenu } = await import('@/lib/menu-utils');

  const [, url] = await Promise.all([
    waitForMenu(),
    (async () => {
      await waitForMenu();
      return getMicroFrontendUrl(route);
    })(),
  ]);

  if (!url) {
    throw new NotFoundError(`Micro frontend não encontrado: ${route}`, route);
  }

  return await loadMicroFrontend(route, url);
}

export function isMicroFrontendCached(route: string): boolean {
  const moduleKey = `${route}/miniapp`;
  return moduleCache.has(moduleKey);
}

export function isMicroFrontendLoading(route: string): boolean {
  for (const [cacheKey] of promiseCache.entries()) {
    if (cacheKey.startsWith(route)) {
      return true;
    }
  }
  return false;
}

export function getMicroFrontendStatus(route: string): {
  isCached: boolean;
  isLoading: boolean;
  isRegistered: boolean;
} {
  return {
    isCached: isMicroFrontendCached(route),
    isLoading: isMicroFrontendLoading(route),
    isRegistered: registeredRemotes.has(route),
  };
}

export function clearMicroFrontendCache(route: string): void {
  const moduleKey = `${route}/miniapp`;
  if (moduleCache.has(moduleKey)) {
    moduleCache.delete(moduleKey);
  }

  for (const [cacheKey] of promiseCache.entries()) {
    if (cacheKey.startsWith(route)) {
      promiseCache.delete(cacheKey);
    }
  }

  if (registeredRemotes.has(route)) {
    registeredRemotes.delete(route);
  }

  if (preloadedRemotes.has(route)) {
    preloadedRemotes.delete(route);
  }
}
